/** @fileoverview Types and pure helpers for selecting and annotating elements in the app preview. */

export type PreviewSelectionItem = {
  path: string;
  data: string;
  definitionPath?: string;
  truncated?: boolean;
  /** 1-based number of the annotated selection group this element belongs to. */
  group?: number;
  /** The user's note for the group; carried on the group's first element only. */
  note?: string;
};

/** Longest comment a user can write on one selection; the backend enforces the same limit. */
export const MAX_SELECTION_NOTE_LENGTH = 1000;
/** Most elements one message can carry; the backend enforces the same limit. */
const MAX_SELECTION_ITEMS = 40;
/** Size budget for a message's selection, kept under the backend's character limit. */
const MAX_SELECTION_BYTES = 24_000;
const SELECTION_BYTES_MARGIN = 32;
/** Group number given to elements that were sent without one, so they stay distinct. */
const UNGROUPED_BASE = 1000;

const itemKey = (item: PreviewSelectionItem) => `${item.group ?? ''}|${item.path}`;

export const previewSelectionLabel = ({ path, data }: PreviewSelectionItem) => {
  const label = data
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  if (label) return label.length > 24 ? `${label.slice(0, 23)}…` : label;
  const segments = path.split('/').filter(Boolean);
  const index = Number(segments.at(-1));
  if (segments.at(-2) === 'items' && Number.isInteger(index)) return `Item ${index + 1}`;
  if (segments.at(-2) === 'inputs' && Number.isInteger(index)) return `Form field ${index + 1}`;
  if (segments.at(-2) === 'columns' && Number.isInteger(index)) return `Column ${index + 1}`;
  if (segments.at(-2) === 'rows') return `Row ${segments.at(-1)}`;
  if (segments.at(-2) === 'cells' && Number.isInteger(index)) return `Cell ${index + 1}`;
  if (segments.at(-2) === 'sections' && Number.isInteger(index)) return `Form section ${index + 1}`;
  if (segments.at(-2) === 'components' && Number.isInteger(index)) return `Component ${index + 1}`;
  return segments.at(-1) || path;
};

export type SelectionAnnotation = { group: number; note?: string; labels: string[] };

/** Groups a sent selection back into its numbered annotations, for showing in the chat. */
export const annotationsFromSelection = (items: PreviewSelectionItem[]): SelectionAnnotation[] => {
  const byGroup = new Map<number, SelectionAnnotation>();
  items.forEach((item, index) => {
    const group = item.group ?? UNGROUPED_BASE + index;
    const annotation = byGroup.get(group) ?? { group, labels: [] };
    const label = previewSelectionLabel(item);
    if (!annotation.labels.includes(label)) annotation.labels.push(label);
    if (item.note && !annotation.note) annotation.note = item.note;
    byGroup.set(group, annotation);
  });
  return [...byGroup.values()].sort((first, second) => first.group - second.group);
};

/** A short stand-in title for a conversation that starts with selections and no text. */
export const selectionTitle = (items: PreviewSelectionItem[]) => {
  const [first] = annotationsFromSelection(items);
  return first?.note || (first ? `Changes to ${first.labels[0]}` : 'Selected elements');
};

type SnapshotRequester = () => Promise<PreviewSelectionItem[]>;
let snapshotRequester: SnapshotRequester | undefined;

/** Registers the live preview as the source of selection snapshots; call the result to release it. */
export const setPreviewSnapshotRequester = (requester: SnapshotRequester) => {
  snapshotRequester = requester;
  return () => {
    if (snapshotRequester === requester) snapshotRequester = undefined;
  };
};
export const requestPreviewSnapshot = () => snapshotRequester?.() ?? Promise.resolve([]);

export const jsonPointerSegment = (value: string | number) =>
  String(value).replace(/~/g, '~0').replace(/\//g, '~1');

export type SelectionRect = { left: number; right: number; top: number; bottom: number };
export const intersectsSelectionRect = (target: SelectionRect, selection: SelectionRect) =>
  target.left < selection.right &&
  target.right > selection.left &&
  target.top < selection.bottom &&
  target.bottom > selection.top;
export const isSelectionRectVisible = (
  target: SelectionRect,
  viewport: SelectionRect,
  clips: SelectionRect[] = []
) => [viewport, ...clips].every((clip) => intersectsSelectionRect(target, clip));

/**
 * Adds paths to a selection. Among the new paths the most specific one wins over its ancestors,
 * and any existing path that overlaps a new one is replaced.
 */
export const addSelectionPaths = (current: string[], next: string[]) => {
  const additions = [...new Set(next)].filter(
    (path) => !next.some((other) => other !== path && other.startsWith(`${path}/`))
  );
  return [
    ...current.filter(
      (path) =>
        !additions.some(
          (added) => path === added || path.startsWith(`${added}/`) || added.startsWith(`${path}/`)
        )
    ),
    ...additions,
  ];
};

/**
 * Replaces the selected children of a container with the container itself once every eligible
 * child is selected, repeating up the tree.
 */
export function collapseCompleteSelectionContainers(
  selectedPaths: string[],
  allTargets: HTMLElement[],
  allowSingleChild = false,
  isEligible: (target: HTMLElement) => boolean = () => true
): string[] {
  // Hidden or clipped targets can never be selected, so they must not block their parent from
  // counting as fully selected.
  const targets = allTargets.filter(isEligible);
  const selected = new Set(selectedPaths);
  const targetSet = new Set(targets);
  const children = new Map<HTMLElement, HTMLElement[]>();
  targets.forEach((target) => {
    let parent = target.parentElement;
    while (parent && !targetSet.has(parent)) parent = parent.parentElement;
    if (parent) children.set(parent, [...(children.get(parent) ?? []), target]);
  });
  [...targets].reverse().forEach((container) => {
    const path = container.dataset.fabrikSelectionPath;
    const directChildren = children.get(container) ?? [];
    if (!path || directChildren.length < (allowSingleChild ? 1 : 2)) return;
    const childPaths = directChildren.map((child) => child.dataset.fabrikSelectionPath);
    if (childPaths.every((childPath) => childPath && selected.has(childPath))) {
      childPaths.forEach((childPath) => selected.delete(childPath!));
      selected.add(path);
    }
  });
  return [...selected];
}

export const removeSelectionPath = (paths: string[], path: string) =>
  paths.filter((itemPath) => itemPath !== path);

/** Merges the selections of several queued prompts, renumbering groups so they stay distinct. */
export function aggregateSelection(selections: PreviewSelectionItem[][]): PreviewSelectionItem[] {
  // Number each prompt's groups after those of the one before, then compact them to 1..n.
  let offset = 0;
  const merged = selections.flatMap((items) => {
    const base = offset;
    offset = Math.max(offset, ...items.map((item) => (item.group ?? 0) + base));
    return items.map((item) => (item.group ? { ...item, group: item.group + base } : item));
  });
  const numbers = [...new Set(merged.flatMap(({ group }) => (group ? [group] : [])))].sort(
    (first, second) => first - second
  );
  const compact = new Map(numbers.map((number, index) => [number, index + 1]));
  return boundSelection(
    merged.map((item) => (item.group ? { ...item, group: compact.get(item.group) } : item))
  );
}

/** Drops duplicates and trims a selection to the item and size limits, marking where it was cut. */
export function boundSelection(items: PreviewSelectionItem[]): PreviewSelectionItem[] {
  const unique = [...new Map(items.map((item) => [itemKey(item), item])).values()];
  const bounded: PreviewSelectionItem[] = [];
  const encoder = new TextEncoder();
  let size = 2;
  let truncated = false;
  for (const item of unique) {
    const itemSize = encoder.encode(JSON.stringify(item)).length + (bounded.length ? 1 : 0);
    if (
      bounded.length >= MAX_SELECTION_ITEMS ||
      size + itemSize > MAX_SELECTION_BYTES - SELECTION_BYTES_MARGIN
    ) {
      if (!bounded.length)
        throw new Error('Selected preview data exceeds the allowed payload size.');
      truncated = true;
      break;
    }
    bounded.push(item);
    size += itemSize;
  }
  if (truncated && bounded.length)
    bounded[bounded.length - 1] = { ...bounded[bounded.length - 1], truncated: true };
  return bounded;
}
